import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { captureException } from "@sentry/react"
import { useBgColorContext } from "./context"

import ServiceBrand from "./shared/service_brand"
import StoredField from "app/components/stored_field/index"
import { fullColoredPage } from "./utils/container_background_color"
import { pluralizeStepType } from "shared/lib/utils"
import ErrorMessageForField from "./error_message"
import Spinner from "../../../shared/components/spinner"

import { validateStepField } from "./api"
import { stepFieldShape } from "app/lib/shared_prop_types"
import formatStoredField from "shared/lib/format_stored_field"
import {
  trackSystemStepConfigSkipped,
  trackClickServiceConfigured,
} from "./utils/telemetry"
import { verifyIntendedPromiseRejection } from "./utils/modal_promise"
import { gqlAliasStepModuleName } from "shared/lib/utils"

import {
  attachNewBadgeForMultiServiceAccountButton,
  removeMultiServiceAccountNewBadge,
} from "shared/scripts/new_badge_button"

import "./step_fields_selector.scss"

const parseDefaultStepFieldsValues = (step, stepDefaultStepFields) => {
  try {
    if (Object.keys(stepDefaultStepFields).length === 0) {
      return {}
    } else {
      return JSON.parse(
        stepDefaultStepFields[gqlAliasStepModuleName(step.module_name)]
          ?.defaults_for_trigger || "{}"
      )
    }
  } catch (err) {
    //Send to Sentry. Pass context directly (tag and extra) only available on version 5.16+
    captureException(err)
    if (window.App.window.App.allowDebugErrorMessages) {
      console.warn("Error parsing default action values", err)
    }
  }
}

export default function StepFieldsSelector({
  defaultValueLoader,
  ingredientsMetadata,
  onCreate,
  stage,
  step,
  sourceAppletId,
  stepFields,
  stepType,
  trigger,
  queries,
  editMode,
  addingStep,
  userOwned,
  makeItYourOwnFeaturePrompt,
  makingItTheirOwn,
  changeServiceAccount,
  updateServiceAccountList,
  connectionFinishedUrl,
  userAllowMultipleLiveChannels,
  proFeatureGate,
}) {
  const bgColor = useBgColorContext()
  const stepPlural = pluralizeStepType(stepType)
  const stepSlug = `${stepPlural}/${step.full_module_name}`
  const isFieldEditable = f =>
    !editMode || userOwned || makingItTheirOwn || !f.hidden
  const requiredStepFields = stepFields
    .filter(isFieldEditable)
    .filter(f => f.required)

  const [defaultValues, updateDefaultValues] = useState({})
  const [defaultValuesLoaded, updateDefaultValuesLoaded] = useState(false)
  const fieldsReady = useRef(0)
  const [formEnabled, updateFormEnabled] = useState(false)
  const [fields, updateFields] = useState({})
  const [errors, updateErrors] = useState({})
  const [submittingStep, updateSubmittingStep] = useState(false)
  const [retryOptionsForService, forceRetryForService] = useState(false)
  const [showAloneNewBadge, updateAloneNewBadgeVisibility] = useState(
    userAllowMultipleLiveChannels === true &&
      attachNewBadgeForMultiServiceAccountButton()
  )
  const submitRef = useRef(null)

  useEffect(() => {
    const effect = async () => {
      if (stepType === "action" || stepType === "query") {
        try {
          const response = await defaultValueLoader()
          const newDefaultValues = parseDefaultStepFieldsValues(step, response)
          updateDefaultValues(newDefaultValues)
          let newFields = {}
          Object.entries(newDefaultValues).forEach(([fieldName, value]) => {
            const stepField = stepFields.find(sf => sf.name === fieldName)
            newFields[fieldName] = {
              ...stepField,
              value: [null, undefined].includes(stepField.value)
                ? value
                : stepField.value,
              hidden: stepField.hasOwnProperty("hidden") // true for importedState
                ? stepField.hidden
                : stepField.hideable,
            }
            updateFields(newFields)
          })
        } finally {
          updateDefaultValuesLoaded(true)
        }
      }
    }
    effect()
  }, [])

  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0

    if (hasErrors && submitRef.current) {
      submitRef.current.blur()
    }
  })

  useEffect(() => {
    const composerEl = document.querySelector("#diy-composer")

    composerEl?.scrollIntoView({ block: "start" })
  }, [])

  useEffect(() => {
    fullColoredPage(bgColor)

    return () => {
      fullColoredPage("initial")
    }
  }, [bgColor])

  const onChange = (field, value) => {
    updateFields(previousFields => ({
      ...previousFields,
      [field.name]: {
        ...field,
        value,
        hidden: field.hasOwnProperty("hidden") // true for importedState
          ? field.hidden
          : field.hideable,
      },
    }))

    if (field.field_ui_type === "service_account_select") {
      changeServiceAccount(value)
    }
  }

  const onSubmit = async e => {
    e.preventDefault()
    updateSubmittingStep(true)
    await validateWithGraph()
  }

  const validateWithGraph = async () => {
    try {
      const stepPayload = (step, fields) => ({
        step_identifier: step.full_module_name,
        channel_id: step.service.id,
        live_channel_id: step.live_channel_id,
        // Step validation only needs name and value. Hidden is used only for DIY mutation.
        fields: Object.values(fields)
          .filter(f => f.name !== "_live_channel_id")
          .map(field => ({
            name: field.name,
            hidden: field.hidden,
            value: JSON.stringify(field.value),
          })),
      })

      const payload = {
        [stepType]: stepPayload(step, fields),
        applet_id: sourceAppletId
      }

      if (stepType === "query" || stepType === "action") {
        if (trigger) {
          payload["trigger"] = stepPayload(trigger, trigger.fields)
        }
      }

      if (stepType === "action") {
        if (!queries) {
          console.error(
            `'queries' prop is required when stepType is '${stepType}'`
          )
          updateSubmittingStep(false)
          return
        }
        payload["queries"] = queries.map(query =>
          stepPayload(query, query.fields)
        )
      }

      validateStepField(stepType, payload).then(errors => {
        if (errors?.validation_errors?.stored_fields) {
          return onFailedValidation(
            errors.validation_errors.stored_fields[`/${stepSlug}`]
          )
        }

        onSuccessfulValidation()
      })
    } catch (err) {
      console.error(err)
      updateSubmittingStep(false)
    }
  }

  const onSuccessfulValidation = () => {
    trackClickServiceConfigured({ stepType }) // We want to track only success step configuration

    onCreate(step, fields)
  }

  const onFailedValidation = parsedErrors => {
    updateSubmittingStep(false)
    updateErrors(parsedErrors)
  }

  const onFieldReady = field => {
    if (field.required) {
      fieldsReady.current += 1
    }

    if (fieldsReady.current === requiredStepFields.length) {
      updateFormEnabled(true) // one-way state change
    }
  }

  const componentForField = field => {
    const currentValue = addingStep ? defaultValues[field.name] : field.value

    // So we can submit a form with params
    const fieldName = `fields[${field.name}]`

    let storedField = formatStoredField(stepType, step.full_module_name, field)

    if (storedField.field_subtype === "service_account_select") {
      storedField.options = step.service.live_channels
      storedField.updateOptionsList = liveChannels =>
        updateServiceAccountList(liveChannels)
      storedField.connectionFinishedUrl = connectionFinishedUrl
      storedField.isOnColor = true
      storedField.value = step.live_channel_id
      storedField.showNewBadge = showAloneNewBadge
      storedField.hideNewBadge = () => {
        updateAloneNewBadgeVisibility(false)
        removeMultiServiceAccountNewBadge()
      }
    }

    const disabled = !isFieldEditable(field)

    return (
      <>
        <StoredField
          field={storedField}
          fieldName={fieldName}
          ingredientsMetadata={ingredientsMetadata}
          value={currentValue}
          onReady={() => (isFieldEditable(field) ? onFieldReady(field) : null)}
          onChange={val => onChange(field, val)}
          fieldOwnerServiceId={step.service.module_name}
          fieldOwnerServiceName={step.service.name}
          fieldOwnerAllowMultipleLiveChannels={
            step.service.allow_multiple_live_channels
          }
          userAllowMultipleLiveChannels={userAllowMultipleLiveChannels}
          serviceAccountSelectFieldScope="diy_composer"
          isOnColor
          platformComposer
          disabled={disabled}
          liveChannelId={step.live_channel_id}
          updateFormEnabled={updateFormEnabled}
          shouldForceRetryOptions={retryOptionsForService}
          updateServiceToForceRetry={val => forceRetryForService(val)}
          proFeatureGate={proFeatureGate}
        />
        {disabled && (
          <div styleName="make-it-your-own">
            <span
              styleName="cta"
              onClick={verifyIntendedPromiseRejection(
                makeItYourOwnFeaturePrompt
              )}
              role="button"
            >
              Make it your own
            </span>{" "}
            to edit this field
          </div>
        )}
      </>
    )
  }

  const fieldsList = () => {
    return stepFields
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(stepField => {
        return (
          <li key={stepField.name} className="field">
            <span className="field-name label">{stepField.label}</span>
            <span className="input">
              <ErrorMessageForField errors={errors} field={stepField.name} />
              {componentForField(stepField)}
            </span>
          </li>
        )
      })
  }

  if (stepFields.length === 0) {
    trackSystemStepConfigSkipped({
      serviceModuleName: step.service.module_name,
      stepType,
    })
    onSuccessfulValidation()
  }

  if ((stepType === "action" || stepType == "query") && !defaultValuesLoaded)
    return <Spinner config={{ color: "#fff" }} />

  const doneCTA = submittingStep
    ? `${addingStep ? "Creating" : "Updating"} ${stepType}...`
    : `${addingStep ? "Create" : "Update"} ${stepType}`

  return (
    <>
      <ServiceBrand
        description={step.description}
        image={step.service.image}
        title={step.name}
      />
      <div styleName="step-fields-selector">
        <form onSubmit={onSubmit} className="form">
          <div styleName="step-fields-container">
            <ul className="fields">{fieldsList()}</ul>

            <div styleName="cta">
              <input
                disabled={!formEnabled || submittingStep}
                ref={submitRef}
                type="submit"
                role="button"
                className="button-primary button-on-color"
                value={doneCTA}
                data-track-ifttt-next-event="click"
                data-track-data={JSON.stringify({
                  object_type: `diy_${stage}_created`,
                  object_id: stepType,
                  location_type: "diy_composer",
                })}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

const stepShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  module_name: PropTypes.string.isRequired,
  full_module_name: PropTypes.string.isRequired,
  normalized_module_name: PropTypes.string.isRequired,
  description: PropTypes.string,
  live_channel_id: PropTypes.string.isRequired,
  service: PropTypes.shape({
    allow_multiple_live_channels: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    module_name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    live_channels: PropTypes.array.isRequired,
  }).isRequired,
  fields: PropTypes.object,
})

StepFieldsSelector.propTypes = {
  defaultValueLoader: PropTypes.func,
  ingredientsMetadata: PropTypes.object,
  onCreate: PropTypes.func.isRequired,
  stage: PropTypes.oneOf(["this", "that"]).isRequired,
  step: stepShape.isRequired,
  sourceAppletId: PropTypes.string,
  stepFields: PropTypes.arrayOf(stepFieldShape).isRequired,
  stepType: PropTypes.oneOf(["trigger", "action", "query"]).isRequired,
  trigger: stepShape,
  queries: PropTypes.arrayOf(stepShape),
  editMode: PropTypes.bool.isRequired,
  addingStep: PropTypes.bool.isRequired,
  userOwned: PropTypes.bool.isRequired,
  makeItYourOwnFeaturePrompt: PropTypes.func.isRequired,
  makingItTheirOwn: PropTypes.bool,
  changeServiceAccount: PropTypes.func.isRequired,
  updateServiceAccountList: PropTypes.func.isRequired,
  connectionFinishedUrl: PropTypes.string.isRequired,
  userAllowMultipleLiveChannels: PropTypes.bool,
  proFeatureGate: PropTypes.func,
}

StepFieldsSelector.defaultProps = {
  ingredientsMetadata: {},
  triggerPayload: {},
}
